import { JobStatusNumber, type Job } from "../types/Job";

/**
 * Whether `job` belongs in the scheduling backlog — the server's `/get-unscheduled` formula: Ready,
 * on a deal, on no shift; test jobs only when the viewer shows them.
 */
export function isBacklogJob(job: Job, showTestJobs: boolean): boolean {
	return (
		job.statusNumber === JobStatusNumber.Ready &&
		job.dealIds.length > 0 &&
		!job.shiftId &&
		(showTestJobs || !job.isTestJob)
	);
}

/** Whether `job` belongs in the board's scheduled list — the server's `/get-scheduled-unsampled`
 *  formula: on a shift, not sampled. */
export function isUnsampledScheduledJob(job: Job): boolean {
	return !!job.shiftId && !job.dateSampled;
}

/**
 * `list` with the jobs re-read after a save swapped in: each re-read job replaces its old record
 * where `belongs` keeps it, or is dropped where it no longer belongs (a re-read id that came back
 * with no record is dropped too); a re-read job new to the list is appended. Every other job keeps
 * its place and its identity, so rows and map layers keyed on it skip the work.
 */
export function withRereadJobs(
	list: readonly Job[],
	reread: readonly Job[],
	rereadIds: Iterable<string>,
	belongs: (job: Job) => boolean,
): Job[] {
	const ids = new Set(rereadIds);
	if (ids.size === 0) return list as Job[];
	const fresh = new Map(reread.map((job) => [job.id, job]));
	const next: Job[] = [];
	const placed = new Set<string>();
	for (const job of list) {
		if (!ids.has(job.id)) {
			next.push(job);
			continue;
		}
		const update = fresh.get(job.id);
		if (update && belongs(update) && !placed.has(job.id)) {
			next.push(update);
			placed.add(job.id);
		}
	}
	for (const job of reread) {
		if (!ids.has(job.id) || placed.has(job.id) || !belongs(job)) continue;
		next.push(job);
		placed.add(job.id);
	}
	return next;
}
